import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ImportantNotesProvider } from "./contexts/ImportantNotesContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Prospects from "./pages/Prospects";
import ProspectForm from "./pages/ProspectForm";
import ProspectDetail from "./pages/ProspectDetail";
import Administration from "./pages/Administration";
import PublicFormLogs from "./pages/PublicFormLogs";
import Sauvegarde from "./pages/Sauvegarde";
import Parametres from "./pages/Parametres";
import ProspectsDoublons from "./pages/ProspectsDoublons";
import Agenda from "./pages/Agenda";
import EventDetail from "./pages/EventDetail";
import CarteRDV from "./pages/CarteRDV";
import Messagerie from "./pages/Messagerie";
import Inventaire from "./pages/Inventaire";
import InventaireList from "./pages/InventaireList";
import Corbeille from "./pages/Corbeille";
import Devis from "./pages/Devis";
import DevisDetail from "./pages/DevisDetail";
import Clients from "./pages/Clients";
import Litiges from "./pages/Litiges";
import ClientDetail from "./pages/ClientDetail";
import AgendaDemenagement from "./pages/AgendaDemenagement";
import AOT from "./pages/AOT";
import TarifAOT from "./pages/TarifAOT";
import MonteMeuble from "./pages/MonteMeuble";
import GardeMeuble from "./pages/GardeMeuble";
import Taches from "./pages/Taches";
import ClientsRelancer from "./pages/ClientsRelancer";
import ProspectsSansReponse from "./pages/ProspectsSansReponse";
import LivraisonsAFaire from "./pages/LivraisonsAFaire";
import Urgent from "./pages/Urgent";
import Recherche from "./pages/Recherche";
import JournalAppels from "./pages/JournalAppels";
import CarteGroupage from "./pages/CarteGroupage";
import Facturation from "./pages/Facturation";
import FactureDetail from "./pages/FactureDetail";
import EspaceDemenageurs from "./pages/EspaceDemenageurs";
import MiseADispo from "./pages/MiseADispo";
import FicheDemenageur from "./pages/FicheDemenageur";
import DemenageurForm from "./pages/DemenageurForm";
import LivraisonForm from "./pages/LivraisonForm";
import DevisNonEnvoyes from "./pages/DevisNonEnvoyes";
import OvhImport from "./pages/OvhImport";
import Statistiques from "./pages/Statistiques";
import StatistiquesProspects from "./pages/StatistiquesProspects";
import StatistiquesDevisAcceptation from "./pages/StatistiquesDevisAcceptation";
import StatistiquesLeaderboard from "./pages/StatistiquesLeaderboard";
import StatistiquesProspectsRegagnes from "./pages/StatistiquesProspectsRegagnes";
import StatistiquesCA from "./pages/StatistiquesCA";
import StatistiquesTresorerie from "./pages/StatistiquesTresorerie";
import StatistiquesActivite from "./pages/StatistiquesActivite";
import StatistiquesBenefice from "./pages/StatistiquesBenefice";
import { useEffect } from "react";
import { toast } from "sonner";
import { runBootHygiene, installAutoUpdate } from "./utils/appHygiene";
import { installDomSafePatches } from "./utils/domSafePatches";
import ErrorBoundary from "./components/ErrorBoundary";

// Install the DOM-mutation safety net IMMEDIATELY at module load — before
// React renders anything — so the very first portal mount/unmount on slow
// devices is already protected.
installDomSafePatches();

function App() {
    // One-shot cleanup on app boot + periodic version polling so that a
    // redeploy is picked up without asking users to clear their cache.
    useEffect(() => {
        runBootHygiene();
        const stop = installAutoUpdate({
            onUpdateAvailable: () => {
                toast.info('Nouvelle version disponible — rechargement automatique dans 10s', {
                    duration: 8000,
                });
            },
        });
        return stop;
    }, []);

    return (
        <AuthProvider>
            <ImportantNotesProvider>
            <BrowserRouter>
                <ErrorBoundary>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/demenageur-form/:token" element={<DemenageurForm />} />
                    <Route path="/livraison-form/:token" element={<LivraisonForm />} />
                    <Route 
                        path="/dashboard" 
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/prospects" 
                        element={
                            <ProtectedRoute>
                                <Prospects />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/prospects/nouveau" 
                        element={
                            <ProtectedRoute>
                                <ProspectForm />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/prospects/:id" 
                        element={
                            <ProtectedRoute>
                                <ProspectDetail />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/prospects/:id/modifier" 
                        element={
                            <ProtectedRoute>
                                <ProspectForm />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/agenda" 
                        element={
                            <ProtectedRoute>
                                <Agenda />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/agenda/:id" 
                        element={
                            <ProtectedRoute>
                                <EventDetail />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/agenda/:id/inventaire" 
                        element={
                            <ProtectedRoute>
                                <Inventaire />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/carte-rdv" 
                        element={
                            <ProtectedRoute>
                                <CarteRDV />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/messagerie" 
                        element={
                            <ProtectedRoute>
                                <Messagerie />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/inventaire" 
                        element={
                            <ProtectedRoute>
                                <InventaireList />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/administration" 
                        element={
                            <ProtectedRoute adminOnly>
                                <Administration />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/administration/parametres" 
                        element={
                            <ProtectedRoute adminOnly>
                                <Parametres />
                            </ProtectedRoute>
                        } 
                    />
                    <Route
                        path="/administration/doublons"
                        element={
                            <ProtectedRoute adminOnly>
                                <ProspectsDoublons />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/administration/logs-formulaire-site"
                        element={
                            <ProtectedRoute adminOnly>
                                <PublicFormLogs />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/administration/sauvegarde"
                        element={
                            <ProtectedRoute adminOnly>
                                <Sauvegarde />
                            </ProtectedRoute>
                        }
                    />
                    <Route 
                        path="/import-ovh" 
                        element={
                            <ProtectedRoute adminOnly>
                                <OvhImport />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/corbeille" 
                        element={
                            <ProtectedRoute adminOnly>
                                <Corbeille />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/devis" 
                        element={
                            <ProtectedRoute>
                                <Devis />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/devis/:id" 
                        element={
                            <ProtectedRoute>
                                <DevisDetail />
                            </ProtectedRoute>
                        } 
                    />
                    <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
                    <Route path="/clients/litiges" element={<ProtectedRoute><Litiges /></ProtectedRoute>} />
                    <Route path="/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
                    <Route path="/taches" element={<ProtectedRoute><Taches /></ProtectedRoute>} />
                    <Route path="/clients-relancer" element={<ProtectedRoute><ClientsRelancer /></ProtectedRoute>} />
                    <Route path="/prospects-sans-reponse" element={<ProtectedRoute><ProspectsSansReponse /></ProtectedRoute>} />
                    <Route path="/livraisons-a-faire" element={<ProtectedRoute><LivraisonsAFaire /></ProtectedRoute>} />
                    <Route path="/urgent" element={<ProtectedRoute><Urgent /></ProtectedRoute>} />
                    <Route path="/devis-non-envoyes" element={<ProtectedRoute><DevisNonEnvoyes /></ProtectedRoute>} />
                    <Route path="/recherche" element={<ProtectedRoute><Recherche /></ProtectedRoute>} />
                    <Route path="/journal-appels" element={<ProtectedRoute><JournalAppels /></ProtectedRoute>} />
                    <Route path="/agenda-demenagement" element={<ProtectedRoute><AgendaDemenagement /></ProtectedRoute>} />
                    <Route path="/aot" element={<ProtectedRoute><AOT /></ProtectedRoute>} />
                    <Route path="/tarif-aot" element={<ProtectedRoute><TarifAOT /></ProtectedRoute>} />
                    <Route path="/monte-meuble" element={<ProtectedRoute><MonteMeuble /></ProtectedRoute>} />
                    <Route path="/garde-meuble" element={<ProtectedRoute><GardeMeuble /></ProtectedRoute>} />
                    <Route path="/carte-groupage" element={<ProtectedRoute><CarteGroupage /></ProtectedRoute>} />
                    <Route path="/facturation" element={<ProtectedRoute><Facturation /></ProtectedRoute>} />
                    <Route path="/espace-demenageurs" element={<ProtectedRoute><EspaceDemenageurs /></ProtectedRoute>} />
                    <Route path="/mise-a-dispo" element={<ProtectedRoute><MiseADispo /></ProtectedRoute>} />
                    <Route path="/pointage/demenageur/:id" element={<ProtectedRoute><FicheDemenageur /></ProtectedRoute>} />
                    <Route path="/facturation/:id" element={<ProtectedRoute><FactureDetail /></ProtectedRoute>} />
                    <Route path="/statistiques" element={<ProtectedRoute><Statistiques /></ProtectedRoute>} />
                    <Route path="/statistiques/prospects" element={<ProtectedRoute><StatistiquesProspects /></ProtectedRoute>} />
                    <Route path="/statistiques/devis-acceptation" element={<ProtectedRoute><StatistiquesDevisAcceptation /></ProtectedRoute>} />
                    <Route path="/statistiques/leaderboard" element={<ProtectedRoute><StatistiquesLeaderboard /></ProtectedRoute>} />
                    <Route path="/statistiques/prospects-regagnes" element={<ProtectedRoute><StatistiquesProspectsRegagnes /></ProtectedRoute>} />
                    <Route path="/statistiques/chiffre-affaires" element={<ProtectedRoute><StatistiquesCA /></ProtectedRoute>} />
                    <Route path="/statistiques/tresorerie" element={<ProtectedRoute><StatistiquesTresorerie /></ProtectedRoute>} />
                    <Route path="/statistiques/activite" element={<ProtectedRoute><StatistiquesActivite /></ProtectedRoute>} />
                    <Route path="/statistiques/benefice" element={<ProtectedRoute><StatistiquesBenefice /></ProtectedRoute>} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                </ErrorBoundary>
            </BrowserRouter>
            </ImportantNotesProvider>
        </AuthProvider>
    );
}

export default App;
